import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/adminApi";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { Switch } from "../ui/switch";
import ImageUploadField from "./ImageUploadField";

// ----- Field input -----
function FieldInput({ field, value, onChange }) {
    const v = value ?? (field.type === "switch" ? false : field.type === "list" ? [] : field.type === "number" ? 0 : "");
    const cls =
        "bg-black/40 border border-white/10 focus:border-gold rounded-lg px-4 py-2.5 text-white outline-none w-full transition-colors font-inter text-sm";

    if (field.type === "image") {
        return (
            <ImageUploadField
                value={value || ""}
                onChange={onChange}
                testId={`field-${field.key}`}
                helpText={field.helpText}
            />
        );
    }
    if (field.type === "textarea") {
        return (
            <textarea
                value={v}
                onChange={(e) => onChange(e.target.value)}
                rows={field.rows || 3}
                placeholder={field.placeholder}
                className={`${cls} resize-y`}
                data-testid={`field-${field.key}`}
            />
        );
    }
    if (field.type === "switch") {
        return (
            <div className="flex items-center gap-3">
                <Switch
                    checked={!!v}
                    onCheckedChange={onChange}
                    data-testid={`field-${field.key}`}
                />
                <span className="font-inter text-sm text-zinc-400">
                    {v ? "Visible" : "Hidden"}
                </span>
            </div>
        );
    }
    if (field.type === "number") {
        return (
            <input
                type="number"
                value={v}
                onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
                placeholder={field.placeholder}
                className={cls}
                data-testid={`field-${field.key}`}
            />
        );
    }
    if (field.type === "select") {
        return (
            <select
                value={v}
                onChange={(e) => onChange(e.target.value)}
                className={cls}
                data-testid={`field-${field.key}`}
            >
                <option value="">{field.placeholder || "Select…"}</option>
                {(field.options || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                ))}
            </select>
        );
    }
    if (field.type === "list") {
        const text = Array.isArray(v) ? v.join("\n") : "";
        return (
            <textarea
                value={text}
                onChange={(e) =>
                    onChange(
                        e.target.value
                            .split("\n")
                            .map((s) => s.trim())
                            .filter(Boolean),
                    )
                }
                rows={field.rows || 5}
                placeholder={field.placeholder || "One item per line"}
                className={`${cls} resize-y font-mono`}
                data-testid={`field-${field.key}`}
            />
        );
    }
    return (
        <input
            type="text"
            value={v}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={cls}
            data-testid={`field-${field.key}`}
        />
    );
}

// ----- Modal form -----
function ResourceFormDialog({ open, onClose, mode, fields, initialValues, onSubmit, title }) {
    const [values, setValues] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            const seed = {};
            for (const f of fields) {
                seed[f.key] = initialValues?.[f.key] ?? f.defaultValue ??
                    (f.type === "switch" ? true : f.type === "list" ? [] : f.type === "number" ? 0 : "");
            }
            setValues(seed);
        }
    }, [open, fields, initialValues]);

    const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        // basic validation
        for (const f of fields) {
            if (f.required && (values[f.key] === "" || values[f.key] === undefined || values[f.key] === null)) {
                toast.error(`${f.label} is required`);
                return;
            }
        }
        setSubmitting(true);
        try {
            await onSubmit(values);
            onClose();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Save failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                data-testid="resource-form-dialog"
                className="bg-[#070707] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-poppins text-white text-xl">
                        {mode === "edit" ? `Edit ${title}` : `Add ${title}`}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-5">
                    {fields.map((f) => (
                        <div key={f.key}>
                            <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                                {f.label}
                                {f.required && <span className="text-gold ml-1">*</span>}
                            </label>
                            <FieldInput
                                field={f}
                                value={values[f.key]}
                                onChange={(v) => setField(f.key, v)}
                            />
                            {f.helpText && f.type !== "image" && (
                                <p className="mt-1.5 font-inter text-xs text-zinc-500">{f.helpText}</p>
                            )}
                        </div>
                    ))}
                    <DialogFooter className="gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 rounded-full border border-white/10 text-zinc-300 hover:text-white hover:border-white/30 font-inter text-sm transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="resource-form-submit"
                            className="inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-60 text-black font-medium font-inter px-6 py-2.5 rounded-full transition-colors"
                        >
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                            {mode === "edit" ? "Save changes" : "Create"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ----- Confirm delete -----
function ConfirmDeleteDialog({ open, onClose, onConfirm, label }) {
    const [busy, setBusy] = useState(false);
    const handle = async () => {
        setBusy(true);
        try {
            await onConfirm();
            onClose();
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="bg-[#070707] border border-white/10 text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-poppins text-white text-lg">Delete this item?</DialogTitle>
                </DialogHeader>
                <p className="font-inter text-sm text-zinc-400">
                    This will permanently remove <span className="text-white">{label || "the item"}</span>. This cannot be undone.
                </p>
                <DialogFooter className="gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="px-5 py-2.5 rounded-full border border-white/10 text-zinc-300 hover:text-white font-inter text-sm transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handle}
                        disabled={busy}
                        data-testid="resource-confirm-delete"
                        className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-medium font-inter px-6 py-2.5 rounded-full transition-colors"
                    >
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={15} />}
                        Delete
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ----- Main -----
export const ResourceManager = ({
    resourceKey,           // e.g. "portfolio"
    title,                 // e.g. "Portfolio Items"
    singular,              // e.g. "Portfolio Item"
    description,           // header description
    endpoint,              // e.g. "/admin/portfolio"
    fields,                // form schema
    renderItem,            // (item) => <jsx>
    canCreate = true,
    canDelete = true,
    canToggleVisible = true,
    visibilityKey = "visible",
}) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState("add");
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get(endpoint);
            setItems(data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint]);

    const onAdd = () => {
        setFormMode("add");
        setEditing(null);
        setFormOpen(true);
    };

    const onEdit = (item) => {
        setFormMode("edit");
        setEditing(item);
        setFormOpen(true);
    };

    const onSubmitForm = async (values) => {
        if (formMode === "edit") {
            await adminApi.put(`${endpoint}/${editing.id}`, values);
            toast.success(`${singular} updated`);
        } else {
            await adminApi.post(endpoint, values);
            toast.success(`${singular} created`);
        }
        await reload();
    };

    const onDelete = async () => {
        if (!deleting) return;
        await adminApi.delete(`${endpoint}/${deleting.id}`);
        toast.success(`${singular} deleted`);
        await reload();
    };

    const onToggleVisibility = async (item) => {
        const next = !item[visibilityKey];
        try {
            await adminApi.put(`${endpoint}/${item.id}`, { [visibilityKey]: next });
            setItems((prev) =>
                prev.map((it) => (it.id === item.id ? { ...it, [visibilityKey]: next } : it)),
            );
            toast.success(next ? "Now visible on site" : "Hidden from site");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Update failed");
        }
    };

    const sortedItems = useMemo(
        () => [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
        [items],
    );

    return (
        <div data-testid={`manager-${resourceKey}`} className="p-6 md:p-10 max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-poppins font-semibold text-white text-2xl md:text-3xl tracking-tight">
                        {title}
                    </h1>
                    {description && (
                        <p className="mt-2 font-inter text-sm text-zinc-400 max-w-2xl">{description}</p>
                    )}
                </div>
                {canCreate && (
                    <button
                        type="button"
                        onClick={onAdd}
                        data-testid={`add-${resourceKey}-btn`}
                        className="inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter text-sm px-5 py-2.5 rounded-full transition-colors"
                    >
                        <Plus size={16} />
                        Add {singular}
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex items-center gap-3 text-zinc-500">
                    <Loader2 size={16} className="animate-spin" /> Loading…
                </div>
            ) : sortedItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-12 text-center">
                    <p className="font-inter text-zinc-400">No {title.toLowerCase()} yet.</p>
                    {canCreate && (
                        <button
                            type="button"
                            onClick={onAdd}
                            className="mt-4 inline-flex items-center gap-2 text-gold hover:underline font-inter text-sm"
                        >
                            <Plus size={14} /> Add the first one
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    <AnimatePresence>
                        {sortedItems.map((item) => (
                            <motion.div
                                key={item.id}
                                layout
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.25 }}
                                data-testid={`item-${resourceKey}-${item.id}`}
                                className="bg-white/[0.02] border border-white/5 hover:border-white/10 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-4 md:items-center"
                            >
                                <div className="flex-1 min-w-0">
                                    {renderItem(item)}
                                </div>
                                <div className="flex items-center gap-2 self-start md:self-center">
                                    {canToggleVisible && (
                                        <button
                                            type="button"
                                            onClick={() => onToggleVisibility(item)}
                                            data-testid={`toggle-${resourceKey}-${item.id}`}
                                            title={item[visibilityKey] ? "Hide from site" : "Show on site"}
                                            className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${
                                                item[visibilityKey]
                                                    ? "border-gold/30 text-gold hover:bg-gold/10"
                                                    : "border-white/10 text-zinc-600 hover:text-zinc-300"
                                            }`}
                                        >
                                            {item[visibilityKey] ? <Eye size={15} /> : <EyeOff size={15} />}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => onEdit(item)}
                                        data-testid={`edit-${resourceKey}-${item.id}`}
                                        title="Edit"
                                        className="w-9 h-9 rounded-lg border border-white/10 text-zinc-300 hover:text-gold hover:border-gold/40 flex items-center justify-center transition-colors"
                                    >
                                        <Pencil size={15} />
                                    </button>
                                    {canDelete && (
                                        <button
                                            type="button"
                                            onClick={() => setDeleting(item)}
                                            data-testid={`delete-${resourceKey}-${item.id}`}
                                            title="Delete"
                                            className="w-9 h-9 rounded-lg border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/40 flex items-center justify-center transition-colors"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            )}

            <ResourceFormDialog
                open={formOpen}
                onClose={() => setFormOpen(false)}
                mode={formMode}
                fields={fields}
                initialValues={editing}
                onSubmit={onSubmitForm}
                title={singular}
            />

            <ConfirmDeleteDialog
                open={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={onDelete}
                label={deleting?.title || deleting?.name}
            />
        </div>
    );
};

export default ResourceManager;
